import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../store/index";

import {
  selectCurrentUser,
  selectToken,
  selectSessionExpiryTime,
  clearAuthenticatedUserSession,
} from "../store/auth-state-slice";
import { PUBLIC_ROUTES } from "../constants/routes";
import { useGetHistoryQuery } from "../requests/history";
import { PAGE_SIZE } from "../constants";

let timerRef: ReturnType<typeof setTimeout>;

export function useMemoizedAuthenticatedSelector() {
  const { email } = useAppSelector(selectCurrentUser);
  const expiryTime = useAppSelector(selectSessionExpiryTime);
  const token = useAppSelector(selectToken);
  return useMemo(
    () => ({ email, token, expiryTime }),
    [email, token, expiryTime]
  );
}

export function useTerminateCurrentSessionAndLogOut() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return function logout() {
    dispatch(clearAuthenticatedUserSession());
    navigate(PUBLIC_ROUTES.MARKETPLACE, { replace: true });
  };
}

export function useIsLoggedIn() {
  const { token } = useMemoizedAuthenticatedSelector();
  return !!token;
}


// handles multistep process
export const useStep = (number: number) => {
  const [step, setStep] = useState(number);
  const prevStep = () => setStep(step - 1);
  const nextStep = () => setStep(step + 1);
  return { step, prevStep, nextStep, setStep };
};

export function useTimeout(
  callbackFunction: () => void,
  timeout?: number
): void {
  const savedCallback = useRef(callbackFunction);

  useEffect(() => {
    savedCallback.current = callbackFunction;
  }, [callbackFunction]);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => savedCallback.current(),
      timeout ?? 3000
    );
    return () => clearTimeout(timeoutId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);

  const copyTextToClipboard = async (text: string) => {
    clearTimeout(timerRef);
    if (!navigator.clipboard) return;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    timerRef = setTimeout(() => setCopied(false), 1500);
  };

  return { copied, copyTextToClipboard };
}

export function usePageNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const navigateWithSearchParam = (
    paramName: string | number,
    paramValue: string | number
  ) => {
    const queryParam = new URLSearchParams(searchParams);
    queryParam.set(String(paramName), paramValue.toString());
    navigate(`${location.pathname}?${queryParam.toString()}`);
  };

  return { navigateWithSearchParam };
}

type ReportHistoryParamsType = {
  name: string;
  startDate?: string;
  endDate?: string;
  page?: string;
  search?: string;
  status?: string;
  transactionDirection?: string;
  responseStatus?: string;
};

export function useGetHistory({
  name,
  startDate,
  endDate,
  search,
  status,
  page,
  transactionDirection,
  responseStatus,
}: ReportHistoryParamsType) {
  const {
    data: reportsData,
    refetch,
    isFetching,
    isLoading,
    isError,
  } = useGetHistoryQuery({
    name,
    startDate,
    endDate,
    search,
    status,
    transactionDirection: transactionDirection?.toLocaleLowerCase() === "all" ? "" : transactionDirection?.toLocaleUpperCase(),
    pageSize: PAGE_SIZE.toString(),
    pageNumber: page,
  });

  const getHistory = useMemo(() => {
    if (reportsData && reportsData.data) {

      if (reportsData?.data?.content) {
        return reportsData?.data?.content;
      } else {
        return []
      }
    }
    return [];
  }, [reportsData]);

  const getCount = useMemo(() => {
    if (reportsData && reportsData.data) {
      return reportsData?.data?.totalElements ?? 100;
    }
    return 0;
  }, [reportsData]);

  return { getHistory, getCount, refetch, isFetching, isLoading, isError };
}
